import { Input } from "antd";
import { type TextAreaProps } from "antd/lib/input";
import type { TextAreaRef } from "antd/lib/input/TextArea";
import { memo, useEffect, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { ShowCharactersRemainingThreshold } from "types/constants";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import {
    type IInputDebouncingContext,
    useDebouncedOnChange,
} from "commonComponents/utilities/Input/InputDebouncingContext";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./BTTextArea.less";

type OmittedTextAreaProps = "id" | "onChange" | "onBlur" | "autosize";
export interface IBTTextAreaProps<FormValues> extends Omit<TextAreaProps, OmittedTextAreaProps> {
    id: KeyOfOrString<FormValues> & string;
    "data-testid": string;
    readOnly?: boolean;

    value: string | undefined;

    /** Placeholder text to display when the textarea is empty */
    placeholder?: string;

    /** The max number of characters to allow in the textarea */
    maxLength?: number;

    /** The minimum number of rows to display */
    minRows?: number;

    /** The maximum number of rows to display */
    maxRows?: number;

    /** pass formik setFieldValue */
    onChange: (field: KeyOfOrString<FormValues>, value: string) => void;

    /** pass formik setFieldTouched */
    onBlur: (id: KeyOfOrString<FormValues>, touched: boolean, value: string | undefined) => void;

    /** Ref for the input */
    fieldRef?: React.RefObject<TextAreaRef>;

    /** Allows adjustment/turning off of debouncing on inputs. { useDebouncing: true, inputDelay: 150ms } is the default. */
    debouncingContext?: IInputDebouncingContext;

    /** Blur on change is used to update remaining characters for the text area, but can cause performance issues.
     * @default true
     */
    blurOnChange?: boolean;

    /** Optional class name for the characters remaining span */
    charsRemainingClassName?: string;

    /**
     * Property that determines the form mode of the textarea. If "click-to-edit", then the textarea content is styled
     * like a similar "readonly" component, becoming editable on focus; suitable for auto-save scenarios.
     * @default "always-editing"
     */
    formMode?: "always-editing" | "click-to-edit";

    /**
     * Controls auto-sizing behavior for the textarea using a shadow element technique.
     * - "width": The textarea width will auto-size to match content. Useful for inline editing scenarios.
     * - "height": The textarea height will auto-size to match content. Bypasses Ant Design's autoSize which can have timing issues.
     * - undefined: No auto-sizing (default).
     *
     * NOTE: You can't use "width" and "height" simultaneously - pick one dimension to auto-resize.
     * @default undefined
     */
    autoResize?: "width" | "height";

    /**
     * Placeholder to show when the textarea is focused. If not provided, uses the regular placeholder.
     */
    focusedPlaceholder?: string;
}

/**
 * @example <BTTextArea<IFormValues> id="XX" data-testid="XX" value={values.XX} onChange={handleChange} onBlur={handleBlur} />
 * @example // max length example using yup validation
 * myText: yup.string().label("My Text").max(4000)
 */
const BTTextAreaInternal = track((props) => ({
    element: "Text Area",
    uniqueId: props["data-testid"],
}))(function <FormValues = undefined>({
    id,
    disabled,
    className,
    onChange,
    onBlur,
    onFocus,
    maxLength,
    value,
    "data-testid": testid,
    readOnly,
    fieldRef,
    minRows = 2,
    maxRows = 12,
    debouncingContext,
    blurOnChange = true,
    charsRemainingClassName,
    formMode = "always-editing",
    autoResize,
    placeholder,
    focusedPlaceholder,
    ...otherProps
}: IBTTextAreaProps<FormValues> & ITrackingProp) {
    const [debouncedValue, debouncedChange, flushDebouncedChange] = useDebouncedOnChange<
        typeof value,
        FormValues
    >(
        value ?? "",
        (field: KeyOfOrString<FormValues>, value: string) => {
            // Sad Cameron here: Why wasn't this added to the consumer's onchange? :(
            // fire blur event on change so min/max length error appears right away
            if (blurOnChange) {
                onBlur(field, true, value);
            }
            onChange(field, value);
        },
        debouncingContext,
    );

    // Only track focus state when in click-to-edit mode
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setIsFocused(false);
    }, [formMode]);

    const isClickToEdit = formMode === "click-to-edit";

    function onChangeWrapper(event: React.ChangeEvent<HTMLTextAreaElement>) {
        debouncedChange(id, event.target.value);
    }

    function onBlurWrapper() {
        setIsFocused(false);
        flushDebouncedChange();
        onBlur(id, true, debouncedValue);
        otherProps.tracking?.trackEvent({ event: "InputBlur" });
    }

    function onFocusWrapper(event: React.FocusEvent<HTMLTextAreaElement>) {
        setIsFocused(true);
        if (onFocus) {
            onFocus(event);
        }
        otherProps.tracking?.trackEvent({ event: "InputFocus" });
    }

    const isClickToEditViewMode = isClickToEdit && !isFocused && !readOnly;

    // Default placeholder to "--" when in click-to-edit mode and no placeholder provided
    const basePlaceholder = isClickToEdit && !placeholder ? "--" : placeholder;

    // Use focusedPlaceholder when focused, otherwise use basePlaceholder
    const renderedPlaceholder =
        isFocused && focusedPlaceholder ? focusedPlaceholder : basePlaceholder;

    // For auto-sizing shadow element, use the actual placeholder or minimum 3 chars
    const shadowContent = debouncedValue || renderedPlaceholder || "---";

    let charactersRemaining: React.ReactElement | undefined;

    const valueLength = debouncedValue ? debouncedValue.length : 0;
    if (
        maxLength !== undefined &&
        disabled !== true &&
        valueLength <= maxLength &&
        valueLength / maxLength >= ShowCharactersRemainingThreshold
    ) {
        // Note: characters remaining past the max length limit should be handled by a max length validator on your form values
        const charCount = maxLength - valueLength;
        charactersRemaining = (
            <span data-testid="charsRemaining" className={charsRemainingClassName}>
                {charCount} {charCount === 1 ? "character" : "characters"} remaining
            </span>
        );
    }

    if (readOnly) {
        return (
            <ValueDisplay
                id={id}
                data-testid={testid}
                value={debouncedValue}
                className={cn(className, {
                    // To make it easier on consumers, apply padding for readonly mode when in click-to-edit form mode
                    "padding-horizontal-xs": formMode === "click-to-edit",
                })}
                isTextArea={true}
            />
        );
    }

    // note: maxLength is NOT spread on the TextArea by design. We want don't want to prevent the user from typing beyond the limit
    // When autoResize is enabled, disable autoSize - the shadow element handles sizing
    const autoSizeConfig = autoResize
        ? false
        : {
              minRows: minRows,
              maxRows: maxRows,
          };

    const textareaElement = (
        // eslint-disable-next-line react/forbid-elements
        <Input.TextArea
            id={id}
            name={id}
            data-testid={testid}
            value={debouncedValue}
            onChange={onChangeWrapper}
            onBlur={onBlurWrapper}
            onFocus={onFocusWrapper}
            autoSize={autoSizeConfig}
            ref={fieldRef}
            disabled={disabled}
            className={cn(autoResize ? undefined : className, {
                "BTTextArea--ClickToEditViewMode": isClickToEditViewMode,
            })}
            placeholder={renderedPlaceholder}
            {...otherProps}
        />
    );

    let textareaContent: React.ReactElement;

    if (autoResize === "width") {
        textareaContent = (
            <div
                className={cn(className, "BTTextArea--AutoWidthWrapper", {
                    "BTTextArea--ClickToEditViewMode": isClickToEditViewMode,
                })}
            >
                <div
                    className={cn("BTTextArea--Shadow", {
                        "BTTextArea--ClickToEditViewMode": isClickToEditViewMode,
                    })}
                    aria-hidden="true"
                >
                    {shadowContent + "\u200B"}
                </div>
                {textareaElement}
            </div>
        );
    } else if (autoResize === "height") {
        textareaContent = (
            <div
                className={cn(className, "BTTextArea--AutoHeightWrapper", {
                    "BTTextArea--ClickToEditViewMode": isClickToEditViewMode,
                })}
            >
                <div
                    className={cn("BTTextArea--Shadow", {
                        "BTTextArea--ClickToEditViewMode": isClickToEditViewMode,
                    })}
                    aria-hidden="true"
                >
                    {shadowContent + "\u200B"}
                </div>
                {textareaElement}
            </div>
        );
    } else {
        textareaContent = textareaElement;
    }

    return (
        <>
            {textareaContent}
            {charactersRemaining}
        </>
    );
});

export const BTTextArea = memo(BTTextAreaInternal) as typeof BTTextAreaInternal;
